import { useState, useEffect } from 'react';
import { Popover, Box, Button, Typography, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import './DateRangePicker.scss';

dayjs.extend(isBetween);

interface Props {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onDateChange: (startDate: Dayjs | null, endDate: Dayjs | null) => void;
  open: boolean;
  anchorEl: HTMLDivElement | null;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
  selectionMode?: 'single' | 'range';
  popoverWidth?: string | number;
  initialMonth?: number;
  initialYear?: number;
  mode?: string;
  disabled?: boolean;
  disableMonthNavigation?: boolean;
}

export default function DateRangePicker({
  startDate,
  endDate,
  onDateChange,
  open,
  anchorEl,
  onClose,
  anchorRef,
  selectionMode = 'range',
  popoverWidth,
  initialMonth,
  initialYear,
  mode,
  disabled,
  disableMonthNavigation
}: Props) {
  const initialMonthIdx =
    initialMonth != null
      ? Math.max(0, Math.min(11, Number(initialMonth) - 1))
      : undefined;
  const [currentMonth, setCurrentMonth] = useState<number>(
    initialMonthIdx != null
      ? initialMonthIdx
      : startDate
        ? startDate.month()
        : dayjs().month()
  );
  const [currentYear, setCurrentYear] = useState<number>(
    initialYear != null
      ? Number(initialYear)
      : startDate
        ? startDate.year()
        : dayjs().year()
  );
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(startDate);
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(endDate);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsSelectingRange(false);
    if (open) {
      if (initialMonthIdx != null && initialYear != null) {
        setCurrentMonth(initialMonthIdx);
        setCurrentYear(Number(initialYear));
      } else if (startDate) {
        setCurrentMonth(startDate.month());
        setCurrentYear(startDate.year());
      }
    }
  }, [startDate, endDate, open, initialMonth, initialYear, initialMonthIdx]);

  const id = open ? 'date-range-popover' : undefined;

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const getDaysInMonth = () => {
    const firstDay = dayjs()
      .year(currentYear)
      .month(currentMonth)
      .startOf('month');

    const daysInMonth = firstDay.daysInMonth();

    const startDayOfWeek = firstDay.day();

    const days: (Dayjs | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(dayjs().year(currentYear).month(currentMonth).date(i));
    }
    return days;
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (date: Dayjs) => {
    if (selectionMode === 'single') {
      setTempStartDate(date);
      setTempEndDate(null);
    } else {
      if (!tempStartDate || (tempStartDate && tempEndDate)) {
        setTempStartDate(date);
        setTempEndDate(null);
        setIsSelectingRange(true);
      } else if (isSelectingRange) {
        if (date.isBefore(tempStartDate, 'day')) {
          setTempStartDate(date);
          setTempEndDate(tempStartDate);
        } else {
          setTempEndDate(date);
        }
        setIsSelectingRange(false);
      }
    }
  };

  const isDateInRange = (date: Dayjs) => {
    if (selectionMode === 'single') return false;
    if (!tempStartDate) return false;

    if (tempEndDate) {
      const start = tempStartDate.startOf('day');
      const end = tempEndDate.startOf('day');
      const current = date.startOf('day');
      return current.isAfter(start) && current.isBefore(end);
    }

    return false;
  };

  const isDateSelected = (date: Dayjs) => {
    if (selectionMode === 'single') {
      return tempStartDate && date.isSame(tempStartDate, 'day');
    }

    const current = date.startOf('day');
    const isStart =
      tempStartDate && current.isSame(tempStartDate.startOf('day'));
    const isEnd = tempEndDate && current.isSame(tempEndDate.startOf('day'));

    return isStart || isEnd;
  };

  const isStartDate = (date: Dayjs) => {
    return (
      tempStartDate && date.startOf('day').isSame(tempStartDate.startOf('day'))
    );
  };

  const isEndDate = (date: Dayjs) => {
    return (
      tempEndDate && date.startOf('day').isSame(tempEndDate.startOf('day'))
    );
  };

  const handleApply = () => {
    onDateChange(tempStartDate, tempEndDate);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleCancel}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
      }}
      PaperProps={{
        style: {
          width:
            popoverWidth ||
            (anchorRef.current ? anchorRef.current.offsetWidth : 'auto'),
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: '0px',
          border: '1px solid #c4c4c4',
          marginTop: '4px'
        }
      }}
    >
      <Box
        className="date-range-popover-content"
        sx={{
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <div className="calendar-header">
          <IconButton
            onClick={handlePreviousMonth}
            size="small"
            className="nav-button"
            disabled={disableMonthNavigation}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" className="month-year-title">
            {`${monthNames[currentMonth]} ${currentYear}`}
          </Typography>
          <IconButton
            onClick={handleNextMonth}
            size="small"
            className="nav-button"
            disabled={disableMonthNavigation}
          >
            <ChevronRight />
          </IconButton>
        </div>
        <div className="calendar-grid">
          <div className="week-days">
            {weekDays.map((day, index) => (
              <div key={index} className="week-day">
                {day}
              </div>
            ))}
          </div>
          <div className="days-grid">
            {getDaysInMonth().map((day, index) => (
              <div
                key={index}
                className={`day-cell ${day ? '' : 'empty'} ${day && isDateInRange(day) ? 'in-range' : ''} ${day && isDateSelected(day) ? 'selected' : ''} ${day && isStartDate(day) ? 'start-date' : ''} ${day && isEndDate(day) ? 'end-date' : ''}`}
                onClick={() => day && handleDateClick(day)}
              >
                {day && <span className="day-number">{day.date()}</span>}
              </div>
            ))}
          </div>
        </div>
        <div className="popover-actions">
          <Button onClick={handleCancel} className="cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            className="apply-button"
          >
            Apply
          </Button>
        </div>
      </Box>
    </Popover>
  );
}
